import { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Paragraph, Separator, Text, XStack, YStack } from 'tamagui';
import { SkeletonList } from '@mrrmlab/ui';
import type { FilteredMessage, MessageEnvelope } from '@mrrmlab/api-client';
import { useMailMessages, useMailTags, useFilteredMessages } from '../mail/hooks';

export function MailMessagesRoute() {
  const { accountId } = useParams({ from: '/mail/$accountId/messages' });
  const { folder } = useSearch({ from: '/mail/$accountId/messages' });
  const navigate = useNavigate();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [hasSuggested, setHasSuggested] = useState(false);
  const [untagged, setUntagged] = useState(false);

  const filterActive = selectedTagIds.length > 0 || hasSuggested || untagged;
  const filterParams = useMemo(
    () => ({ tagIds: selectedTagIds.length ? selectedTagIds : undefined, hasSuggested: hasSuggested || undefined, untagged: untagged || undefined }),
    [selectedTagIds, hasSuggested, untagged],
  );

  const { data, isLoading } = useMailMessages(accountId, folder);
  const { data: allTags = [] } = useMailTags();
  const { data: filteredData, isLoading: filteredLoading } = useFilteredMessages(accountId, folder, filterParams, filterActive);

  function toggleTag(id: string) {
    setSelectedTagIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  const filterBar = (
    <XStack flexWrap="wrap" gap="$2" marginBottom="$4">
      <FilterChip label="Mit Suggested" active={hasSuggested} onPress={() => { setHasSuggested((v) => !v); setUntagged(false); }} />
      <FilterChip label="Ohne Tags" active={untagged} onPress={() => { setUntagged((v) => !v); setHasSuggested(false); }} />
      {allTags.map((tag) => (
        <FilterChip key={tag.id} label={tag.name} active={selectedTagIds.includes(tag.id)} onPress={() => toggleTag(tag.id)} />
      ))}
    </XStack>
  );

  if (filterActive) {
    if (filteredLoading) {
      return (
        <YStack padding="$4" maxWidth={720}>
          <Text fontSize="$6" fontWeight="700" marginBottom="$4">{folder}</Text>
          {filterBar}
          <SkeletonList count={6} />
        </YStack>
      );
    }
    const filtered = filteredData?.messages ?? [];
    return (
      <YStack padding="$4" maxWidth={720}>
        <Text fontSize="$6" fontWeight="700" marginBottom="$4">{folder}</Text>
        {filterBar}
        {!filtered.length ? (
          <Paragraph color="$color10" textAlign="center" marginTop="$4">Keine Nachrichten gefunden.</Paragraph>
        ) : (
          <YStack>
            {filtered.map((message, i) => (
              <YStack key={message.id}>
                {i > 0 && <Separator />}
                <FilteredMessageRow
                  message={message}
                  onPress={() => navigate({ to: '/mail/$accountId/reader', params: { accountId }, search: { folder, uid: message.uid } })}
                />
              </YStack>
            ))}
          </YStack>
        )}
      </YStack>
    );
  }

  if (isLoading) {
    return (
      <YStack padding="$4" maxWidth={720}>
        <Text fontSize="$6" fontWeight="700" marginBottom="$4">{folder}</Text>
        {filterBar}
        <SkeletonList count={8} />
      </YStack>
    );
  }

  if (!data?.messages.length) {
    return (
      <YStack padding="$4" maxWidth={720}>
        <Text fontSize="$6" fontWeight="700" marginBottom="$4">{folder}</Text>
        {filterBar}
        <Paragraph color="$color10" textAlign="center" marginTop="$4">Keine Nachrichten.</Paragraph>
      </YStack>
    );
  }

  return (
    <YStack padding="$4" maxWidth={720}>
      <Text fontSize="$6" fontWeight="700" marginBottom="$4">{folder}</Text>
      {filterBar}
      <YStack>
        {data.messages.map((message, i) => (
          <YStack key={message.uid}>
            {i > 0 && <Separator />}
            <MessageRow
              message={message}
              onPress={() => navigate({ to: '/mail/$accountId/reader', params: { accountId }, search: { folder, uid: message.uid } })}
            />
          </YStack>
        ))}
      </YStack>
    </YStack>
  );
}

function FilterChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <XStack
      paddingHorizontal="$3"
      paddingVertical="$1"
      borderRadius="$10"
      backgroundColor={active ? '$blue5' : '$color3'}
      borderWidth={1}
      borderColor={active ? '$blue7' : '$color7'}
      cursor="pointer"
      hoverStyle={{ opacity: 0.8 }}
      onPress={onPress}
    >
      <Text fontSize="$3" color={active ? '$blue11' : '$color'}>{label}</Text>
    </XStack>
  );
}

function FilteredMessageRow({ message, onPress }: { message: FilteredMessage; onPress: () => void }) {
  const isUnread = !message.flags.includes('\\Seen');
  const from = message.fromAddr?.replace(/<.*>/, '').trim() || '(unbekannt)';
  const date = message.date
    ? new Date(message.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    : '';

  return (
    <XStack
      paddingVertical="$3"
      paddingHorizontal="$2"
      gap="$3"
      hoverStyle={{ backgroundColor: '$color3' }}
      borderRadius="$3"
      cursor="pointer"
      onPress={onPress}
    >
      <YStack flex={1} gap="$1">
        <XStack justifyContent="space-between" gap="$2">
          <Text fontSize="$4" fontWeight={isUnread ? '700' : '400'} flex={1} numberOfLines={1}>{from}</Text>
          <Text fontSize="$3" color="$color10" flexShrink={0}>{date}</Text>
        </XStack>
        <Text fontSize="$4" color={isUnread ? '$color' : '$color10'} numberOfLines={1}>
          {message.subject || '(kein Betreff)'}
        </Text>
        {message.tags.length > 0 && (
          <XStack flexWrap="wrap" gap="$1" marginTop="$1">
            {message.tags.map((tag) => (
              <Text
                key={tag.tagId}
                fontSize="$2"
                paddingHorizontal="$2"
                paddingVertical="$0.5"
                borderRadius="$10"
                backgroundColor={tag.status === 'confirmed' ? '$green3' : '$yellow3'}
                color={tag.status === 'confirmed' ? '$green11' : '$yellow11'}
              >
                {tag.name}
              </Text>
            ))}
          </XStack>
        )}
      </YStack>
    </XStack>
  );
}

function MessageRow({ message, onPress }: { message: MessageEnvelope; onPress: () => void }) {
  const isUnread = !message.flags.includes('\\Seen');
  const from = message.from?.replace(/<.*>/, '').trim() || message.from || '(unbekannt)';
  const date = message.date
    ? new Date(message.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    : '';

  return (
    <XStack
      paddingVertical="$3"
      paddingHorizontal="$2"
      gap="$3"
      hoverStyle={{ backgroundColor: '$color3' }}
      borderRadius="$3"
      cursor="pointer"
      onPress={onPress}
    >
      <YStack flex={1} gap="$1">
        <XStack justifyContent="space-between" gap="$2">
          <Text fontSize="$4" fontWeight={isUnread ? '700' : '400'} flex={1} numberOfLines={1}>{from}</Text>
          <Text fontSize="$3" color="$color10" flexShrink={0}>{date}</Text>
        </XStack>
        <Text fontSize="$4" color={isUnread ? '$color' : '$color10'} numberOfLines={1}>
          {message.subject || '(kein Betreff)'}
        </Text>
      </YStack>
    </XStack>
  );
}
